
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { WorkoutPlanRoutines } from "@/components/routines/workout-plans/WorkoutPlanRoutines";
import { useOptimizedWorkoutPlan } from "@/components/routines/workout-plans/hooks/useOptimizedWorkoutPlan";
import { IntroTour } from "@/components/intro/IntroTour";
import { TutorialButton } from "@/components/intro/TutorialButton";
import { PlanViewShimmer } from "@/components/routines/workout-plans/components/PlanViewShimmer";

const PlanView = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { data: plan, isLoading, error } = useOptimizedWorkoutPlan(planId);
  const [showTutorial, setShowTutorial] = useState(false);

  if (isLoading) {
    return <PlanViewShimmer />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-primary">
        <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="text-center">
            <p className="text-red-400 mb-4">Error al cargar el plan de entrenamiento</p>
            <Button
              variant="outline"
              onClick={() => navigate("/rutina")}
              className="text-primary hover:bg-primary/10 hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Rutinas
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-background text-primary">
        <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="text-center">
            <p className="text-white/60 mb-4">Plan no encontrado</p>
            <Button
              variant="outline"
              onClick={() => navigate("/rutina")}
              className="text-primary hover:bg-primary/10 hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Rutinas
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-primary transition-colors duration-300">
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-safe-area">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Button
            variant="ghost"
            className="text-primary hover:bg-primary/10 hover:text-primary self-start transition-colors"
            onClick={() => navigate("/rutina")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold plan-header text-primary]">{plan.name}</h1>
          <TutorialButton onClick={() => setShowTutorial(true)} />
        </div>

        {plan.description && (
          <p className="text-sm sm:text-base text-primary/60 mb-4 sm:mb-6">{plan.description}</p>
        )}

        <WorkoutPlanRoutines planId={planId} initialPlan={plan} hideBackButton />
        <IntroTour run={showTutorial} setRun={setShowTutorial} />
      </main>
    </div>
  );
};

export default PlanView;
